import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AutoAwesomeMosaicIcon from "@mui/icons-material/AutoAwesomeMosaic";
import PaginatedListLayout from "../../../shared/pagination/PaginatedListLayout";
import SkillSheetMainSummaryList from "./SkillSheetMainSummaryList";
import ConfirmStopModal from "../../../shared/ConfirmStopModal";
import { useIsDesktop } from "../../../shared/hooks/useIsDesktop";
import NoticeBanner from "../../../shared/NoticeBanner";
import ActionButton from "../../../shared/ActionButton";
import { useSkillSheetMainIndex } from "./components/useSkillSheetMainIndex";
import {
  PROJECT_COLOR,
  PROJECT_COLOR_DARK,
  PROJECT_COLOR_LIGHT,
  GRAY_COLOR,
  GRAY_COLOR_LIGHT,
  GRAY_COLOR_DARK,
} from "../../../../constants/colors";

/**
 * プロジェクト一覧の補足説明
 * PC: Tooltip / SP: Dialog
 */
function ProjectHelp() {
  const [open, setOpen] = useState(false);
  const isDesktop = useIsDesktop();

  const content = (
    <>
      同じ企業・同じ案件として見せたいプロジェクトはまとめられます。
      <br />
      関連する案件をまとめることで、短期案件が多く見える印象を防ぎ、実際の経歴を伝わりやすくできます。
    </>
  );

  if (isDesktop) {
    return (
      <Tooltip title={content} arrow>
        <IconButton
          size="small"
          sx={{ color: "text.secondary" }}
          aria-label="参画プロジェクト一覧のヘルプ"
        >
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={() => setOpen(true)}
        aria-label="参画プロジェクト一覧のヘルプ"
      >
        <HelpOutlineIcon fontSize="small" />
      </IconButton>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>参画プロジェクトについて</DialogTitle>

        <DialogContent>
          <Typography sx={{ lineHeight: 1.8 }}>{content}</Typography>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function SkillSheetMainIndex() {
  const isDesktop = useIsDesktop();

  const {
    workHistoryGroups,
    pagination,
    loading,
    grouping,
    page,
    selectedWorkHistoryIds,
    setSelectedWorkHistoryIds,
    isDisabled,
    noticeText,
    actionLabel,
    confirmModalText,
    confirmModal,
    goToPage,
    handleGroupAction,
    closeConfirmModal,
    handleRemoveFromGroup,
    handleUngroup,
    handleConfirmGroupAction,
  } = useSkillSheetMainIndex();

  return (
    <Container
      maxWidth="md"
      disableGutters
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ===== 固定エリア ===== */}
      <Box
        sx={{
          position: "sticky",
          top: isDesktop ? "63px" : "45px",
          zIndex: 20,
          backgroundColor: "#F4F7FA",
          boxShadow: 0.5,
          mx: -2,
          px: isDesktop ? 0 : 2,
          pt: isDesktop ? 1.5 : 1,
        }}
      >
        {/* --- 上段：タイトル / RequestStatus --- */}
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isDesktop ? "row" : "column",
              alignItems: isDesktop ? "center" : "stretch",
              gap: isDesktop ? 1.5 : 1,
            }}
          >
            {/* タイトル */}
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: isDesktop ? "1.8rem" : "1.2rem",
                  whiteSpace: "nowrap",
                }}
              >
                参画プロジェクト一覧
              </Typography>

              <ProjectHelp />
            </Box>

            {/* NoticeBanner + ActionButton */}
            <Box
              sx={{
                ml: isDesktop ? "auto" : 0,
                display: "flex",
                flexDirection: isDesktop ? "row" : "column-reverse",
                gap: 1,
                width: isDesktop ? "auto" : "100%",
                alignItems: "center",
              }}
            >
              <NoticeBanner text={noticeText} />

              <ActionButton
                onClick={handleGroupAction}
                label={actionLabel}
                icon={
                  <AutoAwesomeMosaicIcon
                    sx={{
                      color: isDisabled ? GRAY_COLOR_DARK : PROJECT_COLOR,
                    }}
                  />
                }
                disabled={isDisabled}
                sx={{
                  bgcolor: isDisabled ? GRAY_COLOR : PROJECT_COLOR,
                  color: isDisabled ? GRAY_COLOR_DARK : "#fff",
                  fontWeight: 700,
                  width: isDesktop ? "auto" : "100%",

                  "&:hover": {
                    bgcolor: isDisabled ? GRAY_COLOR : PROJECT_COLOR_DARK,
                  },
                }}
                badgeSx={{
                  bgcolor: isDisabled ? GRAY_COLOR_LIGHT : PROJECT_COLOR_LIGHT,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 一覧 */}
      <Box sx={{ mt: 3 }}>
        <PaginatedListLayout
          items={workHistoryGroups}
          loading={loading}
          pagination={pagination}
          onPageChange={goToPage}
          emptyText="プロジェクトが未登録です。"
        >
          <SkillSheetMainSummaryList
            workHistoryGroups={workHistoryGroups}
            page={page}
            per={pagination?.per_page}
            selectedWorkHistoryIds={selectedWorkHistoryIds}
            onSelectedWorkHistoryIdsChange={setSelectedWorkHistoryIds}
            onRemoveFromGroup={handleRemoveFromGroup}
            onUngroup={handleUngroup}
            processing={grouping}
          />
        </PaginatedListLayout>
      </Box>

      {/* グループ解除・グループから外す確認モーダル */}
      <ConfirmStopModal
        open={confirmModal.open}
        onClose={closeConfirmModal}
        onConfirm={handleConfirmGroupAction}
        title={confirmModalText.title}
        message={confirmModalText.message}
        confirmLabel={confirmModalText.confirmLabel}
      />
    </Container>
  );
}
